import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../i18n/I18nContext';
import { io } from 'socket.io-client';
import { studentsAPI, absencesAPI } from '../../services/api';
import { subscribePushNotifications, unsubscribePushNotifications, getPushSubscription } from '../../services/push';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const BUS_ICON = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const DEFAULT_CENTER = [48.8566, 2.3522];
const DEFAULT_ZOOM = 13;

function MapUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, map.getZoom(), { duration: 0.5 });
  }, [center, map]);
  return null;
}

function ParentDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [busLocation, setBusLocation] = useState(null);
  const [routeId, setRouteId] = useState(null);
  const [studentId, setStudentId] = useState(null);
  const [studentName, setStudentName] = useState('');
  const [trail, setTrail] = useState([]);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [absences, setAbsences] = useState([]);
  const [children, setChildren] = useState([]);
  const [view, setView] = useState('home');

  useEffect(() => {
    async function loadData() {
      try {
        const [sRes, aRes] = await Promise.all([studentsAPI.list(), absencesAPI.list()]);
        const myChildren = sRes.data.filter(s => s.parent_id === user?.id);
        setChildren(myChildren);
        setAbsences(aRes.data);
        if (myChildren.length > 0 && !studentId) {
          const first = myChildren.find(c => c.route_id) || myChildren[0];
          setStudentId(first.id);
          setRouteId(first.route_id);
          setStudentName(`${first.first_name} ${first.last_name}`);
        }
      } catch (err) { }
    }
    loadData();
  }, [user?.id, studentId]);

  useEffect(() => {
    getPushSubscription().then((sub) => setPushEnabled(!!sub));
  }, []);

  const togglePush = async () => {
    if (pushEnabled) { await unsubscribePushNotifications(); setPushEnabled(false); }
    else { const sub = await subscribePushNotifications(); setPushEnabled(!!sub); }
  };

  useEffect(() => {
    if (!routeId) return;
    const socket = io(window.location.origin);
    socket.emit('join_route_tracking', routeId);
    socket.on('bus_location_changed', (data) => {
      setBusLocation({ latitude: data.latitude, longitude: data.longitude });
      setTrail((prev) => [...prev, [data.latitude, data.longitude]]);
    });
    return () => socket.disconnect();
  }, [routeId]);

  const handleNotifyAction = async (studentId, sub_type) => {
    const reason = window.prompt(t('Enter comment (optional):'));
    if (reason === null) return;
    try {
      await absencesAPI.create({ student_id: studentId, date: new Date().toISOString().split('T')[0], reason, type: 'full_day', sub_type });
      const res = await absencesAPI.list();
      setAbsences(res.data);
    } catch (err) { alert('Error notifying action'); }
  };

  const handleUpdatePrimaryLocation = async (studentId, lat, lng, address) => {
    try {
      await studentsAPI.update(studentId, { latitude: lat, longitude: lng, home_address: address });
      alert(t('locationUpdated'));
      window.location.reload();
    } catch (err) { alert('Error'); }
  };

  const handleCancelAbsence = async (absenceId) => {
    try {
      await absencesAPI.remove(absenceId);
      const res = await absencesAPI.list();
      setAbsences(res.data);
    } catch (err) { alert('Error'); }
  };

  const handleLogout = () => { logout(); navigate('/login'); };
  const busPosition = busLocation ? [busLocation.latitude, busLocation.longitude] : null;

  if (view === 'home') {
    return (
      <div style={{ padding: '0', fontFamily: 'Arial, sans-serif', maxWidth: '800px', margin: '0 auto', backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
        <header style={{ padding: '30px 20px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', borderRadius: '0 0 30px 30px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ margin: 0, fontSize: '26px' }}>🏘️ {t('parentApp')}</h1>
            <button onClick={handleLogout} style={{ padding: '10px 20px', background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none', borderRadius: '15px', fontWeight: 'bold' }}>{t('logout')}</button>
          </div>
          <p style={{ marginTop: '10px', opacity: 0.9 }}>{t('welcome')}, <strong>{user?.name}</strong></p>
        </header>
        <div style={{ padding: '20px', display: 'grid', gap: '20px', marginTop: '10px' }}>
          <button onClick={() => setView('track')} style={{ padding: '40px 20px', background: '#fff', border: 'none', borderRadius: '25px', boxShadow: '0 10px 20px rgba(0,0,0,0.05)', textAlign: 'left', cursor: 'pointer' }}>
            <span style={{ fontSize: '30px', display: 'block', marginBottom: '10px' }}>📡</span>
            <strong style={{ fontSize: '20px', display: 'block' }}>{t('trackBus')}</strong>
            <span style={{ fontSize: '14px', color: '#718096' }}>{t('seeLiveLocationOnMap')}</span>
          </button>
          <button onClick={() => setView('notify')} style={{ padding: '40px 20px', background: '#fff', border: 'none', borderRadius: '25px', boxShadow: '0 10px 20px rgba(0,0,0,0.05)', textAlign: 'left', cursor: 'pointer' }}>
            <span style={{ fontSize: '30px', display: 'block', marginBottom: '10px' }}>📢</span>
            <strong style={{ fontSize: '20px', display: 'block' }}>{t('manageAttendance')}</strong>
            <span style={{ fontSize: '14px', color: '#718096' }}>{t('reportAbsencesOrPickups')}</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0', fontFamily: 'Arial, sans-serif', maxWidth: '800px', margin: '0 auto', backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
      <header style={{ padding: '15px 20px', background: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '15px', position: 'sticky', top: 0, zIndex: 100 }}>
        <button onClick={() => setView('home')} style={{ background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: '50%', width: '45px', height: '45px', cursor: 'pointer', fontSize: '20px' }}>⬅️</button>
        <h1 style={{ margin: 0, fontSize: '20px', color: '#2d3748' }}>{view === 'track' ? t('liveTracking') : t('attendance')}</h1>
      </header>
      <div style={{ padding: '20px' }}>
        {children.length > 1 && (
          <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '10px' }}>
            {children.map(c => (
              <button key={c.id} onClick={() => { setStudentId(c.id); setRouteId(c.route_id); setStudentName(`${c.first_name} ${c.last_name}`); setTrail([]); setBusLocation(null); }} style={{ padding: '12px 20px', borderRadius: '20px', border: 'none', background: studentId === c.id ? '#764ba2' : '#fff', color: studentId === c.id ? '#fff' : '#4a5568', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', fontWeight: 'bold' }}>👶 {c.first_name}</button>
            ))}
          </div>
        )}
        {view === 'notify' && (
          <div style={{ background: '#fff', padding: '25px', borderRadius: '25px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
            <h2 style={{ fontSize: '20px', marginBottom: '20px' }}>⚡ {t('quickActions')}</h2>
            {children.filter(c => !studentId || c.id === studentId).map(child => {
              const absence = absences.find(a => a.student_id === child.id && a.date.startsWith(new Date().toISOString().split('T')[0]));
              return (
                <div key={child.id}>
                  <p style={{ color: '#718096', marginBottom: '15px' }}>{t('student')}: <strong>{child.first_name}</strong></p>
                  {absence ? (
                    <div style={{ background: '#fef2e2', padding: '20px', borderRadius: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                       <div><strong style={{ color: '#b91c1c' }}>📢 {t(`type_${absence.sub_type}`)}</strong></div>
                       <button onClick={() => handleCancelAbsence(absence.id)} style={{ padding: '10px 20px', background: '#fff', border: '1px solid #fecaca', borderRadius: '12px', color: '#dc3545', fontWeight: 'bold' }}>{t('cancel')}</button>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: '12px' }}>
                      <button onClick={() => handleNotifyAction(child.id, 'absent')} style={{ padding: '18px', background: '#fef2f2', color: '#e53e3e', border: '1px solid #feb2b2', borderRadius: '20px', fontWeight: 'bold', fontSize: '15px', textAlign: 'left' }}>🚫 {t('reportAbsence')}</button>
                      <button onClick={() => handleNotifyAction(child.id, 'parent_pickup')} style={{ padding: '18px', background: '#f0fff4', color: '#38a169', border: '1px solid #c6f6d5', borderRadius: '20px', fontWeight: 'bold', fontSize: '15px', textAlign: 'left' }}>👨‍👩‍👧 {t('parentWillPickUp')}</button>
                      <button onClick={() => handleNotifyAction(child.id, 'family_pickup')} style={{ padding: '18px', background: '#fffaf0', color: '#d69e2e', border: '1px solid #fef3c7', borderRadius: '20px', fontWeight: 'bold', fontSize: '15px', textAlign: 'left' }}>🏠 {t('familyWillPickUp')}</button>
                      <button onClick={() => {
                        const link = window.prompt(t('Maps link:'));
                        if (link) {
                          const match = link.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
                          if (match) handleUpdatePrimaryLocation(child.id, match[1], match[2], t('Temporary'));
                        }
                      }} style={{ padding: '18px', background: '#ebf8ff', color: '#3182ce', border: '1px solid #bee3f8', borderRadius: '20px', fontWeight: 'bold', fontSize: '15px', textAlign: 'left' }}>📍 {t('changeLocation')}</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {view === 'track' && (
          <div style={{ background: '#fff', padding: '20px', borderRadius: '25px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h2 style={{ fontSize: '20px' }}>📡 {t('liveTracking')}</h2>
              <button onClick={togglePush} style={{ background: pushEnabled ? '#48bb78' : '#cbd5e0', color: '#fff', border: 'none', padding: '8px 15px', borderRadius: '15px', fontWeight: 'bold' }}>{pushEnabled ? '🔔 ON' : '🔕 OFF'}</button>
            </div>
            <div style={{ height: '400px', borderRadius: '20px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
               <MapContainer center={busPosition || DEFAULT_CENTER} zoom={DEFAULT_ZOOM} style={{ height: '100%', width: '100%' }}>
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <MapUpdater center={busPosition} />
                  {busPosition && <Marker position={busPosition} icon={BUS_ICON} />}
                  {trail.length >= 2 && <Polyline positions={trail} color="#48bb78" weight={4} />}
               </MapContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ParentDashboard;
